import { fetch } from "~/utils/helper";
import { buildSteps, isPendingStatus, nextStep, progress } from "~/utils/onboarding";
import { writesNoCode } from "~/utils/welcome";

// The time between two reloads while waiting for something: the processing of a test order
// (a few seconds on the worker side) or the first delivery of a webhook.
const POLL_MS = 5000;

// Several screens mounted at the same time (the sidebar, the page) read the same state: two automatic
// reloads close together count as one. An action by the person, on the other hand, always reloads.
const AUTO_MIN_GAP_MS = 2500;
let requestId = 0;
let inflight = null;
let lastAt = 0;

// Getting-started progress of the current account.
//
// To call in a component's `setup`: polling starts on mount and stops on unmount. The state is
// SHARED between all the components that call it (the sidebar, the getting-started page, the dashboard
// navigation): a step checked in the page shows up right away in the sidebar.
// The milestones come from GET /accounts/:id/onboarding, which answers "this has already happened": a deleted key or a
// removed endpoint therefore unchecks nothing.
//
// `grants: false` for a screen that does not show MCP applications: one call less on mount.
export const useOnboarding = ({ grants: withGrantsAtMount = true } = {}) => {
  const nuxtApp = useNuxtApp();

  const shared = useState(`onboarding:${ nuxtApp.$account?.id }`, () => ({ milestones: null, mcpGrants: 0, loaded: false, failed: false }));
  const milestones = computed(() => shared.value.milestones);
  const mcpGrants = computed(() => shared.value.mcpGrants);
  const loaded = computed(() => shared.value.loaded);
  const failed = computed(() => shared.value.failed);
  const patch = (changes) => { shared.value = { ...shared.value, ...changes }; };

  const hasSubscription = computed(() => !!nuxtApp.$account?.subscription_plan);
  // The answers of the onboarding (/welcome), kept on the account: someone who does not write the code is asked to invite
  // whoever will before anything else.
  const discovery = computed(() => milestones.value?.discovery ?? nuxtApp.$account?.options?.onboarding?.discovery ?? null);
  const steps = computed(() => buildSteps(milestones.value, {
    hasSubscription: hasSubscription.value,
    mcpGrants: mcpGrants.value,
    writesNoCode: writesNoCode(discovery.value)
  }));
  const status = computed(() => progress(steps.value));
  const next = computed(() => nextStep(steps.value));
  const latestTestOrder = computed(() => milestones.value?.latestTestOrder ?? null);
  const complete = computed(() => status.value.done === status.value.total);

  // Stored per account by the API: `completedAt` is the date of the first real order (the goal of
  // getting started), `dismissedAt` the date the guided getting started was left for the full dashboard. They apply to all
  // browsers and all people on the account.
  const completedAt = computed(() => milestones.value?.completedAt ?? null);
  const dismissedAt = computed(() => milestones.value?.dismissedAt ?? null);
  // Finished: all steps, or the first real order (it can be reached without going through the sandbox).
  const finished = computed(() => complete.value || !!completedAt.value);

  // MCP applications rarely change and go through another call: only the screens that show them make it.
  const loadGrants = async () => {
    try {
      const res = await fetch("/mcp/grants");
      patch({ mcpGrants: res?.grants?.length ?? 0 });
    } catch {
      // Without this read the optional « agent IA » step stays unticked: harmless.
    }
  };

  const refresh = async ({ withGrants = false, auto = false } = {}) => {
    // MCP applications first: the getting-started page needs them even if the sidebar has just read the milestones.
    if (withGrants) loadGrants();
    if (auto && (inflight || Date.now() - lastAt < AUTO_MIN_GAP_MS)) return inflight ?? undefined;

    // Only one call wins: a `refresh` fired right after an action must not be overwritten by an older one.
    const id = ++requestId;
    const run = (async () => {
      let response = null;
      try {
        response = await fetch("/accounts/:accountId/onboarding");
      } catch {
        response = null;
      }
      if (id !== requestId) return;
      if (response?.success) patch({ milestones: response.onboarding, failed: false, loaded: true });
      else patch({ failed: true, loaded: true });
    })();
    inflight = run;
    try {
      await run;
    } finally {
      if (inflight === run) inflight = null;
      lastAt = Date.now();
    }
  };

  // Leaves the guided getting started for the full dashboard (`dismissed`), or comes back to it, for the whole account
  // (composables/useSetupMode.js). The screen follows the API response, not the click.
  const toast = useToast();
  const setDismissed = async (dismissed) => {
    try {
      const res = await fetch("/accounts/:accountId/onboarding", { method: "POST", body: { dismissed } });
      if (res?.success && milestones.value) patch({ milestones: { ...milestones.value, dismissedAt: res.dismissedAt ?? null } });
    } catch {
      toast.add({
        id: "onboarding-dismiss",
        title: nuxtApp.$i18n.t("onboarding.mode.failed"),
        description: nuxtApp.$i18n.t("onboarding.mode.failedText"),
        icon: "i-heroicons-x-circle",
        color: "error",
        timeout: 5000
      });
    }
  };

  // As long as the test order is not processed, or an endpoint is waiting for its first delivery, we keep reloading.
  // After an error we stop: the « Réessayer » button takes over, instead of piling up errors.
  const waiting = computed(() => {
    const m = milestones.value;
    if (!m || failed.value) return false;
    return isPendingStatus(m.latestTestOrder?.status) || (m.testOrder && m.webhookEndpoint && !m.webhookDelivered);
  });

  let timer = null;
  const onVisible = () => { if (!document.hidden) refresh({ withGrants: withGrantsAtMount, auto: true }); };

  onMounted(() => {
    refresh({ withGrants: withGrantsAtMount, auto: true });
    timer = setInterval(() => { if (!document.hidden && waiting.value) refresh({ auto: true }); }, POLL_MS);
    document.addEventListener("visibilitychange", onVisible);
  });
  onBeforeUnmount(() => {
    clearInterval(timer);
    document.removeEventListener("visibilitychange", onVisible);
  });

  return {
    milestones, discovery, steps, status, next, latestTestOrder, complete, completedAt, dismissedAt, finished,
    loaded, failed, hasSubscription, refresh, setDismissed
  };
};
